import { PipelineStages, ActivityTypes, ActivityOutcomes, AtRiskReasons, RiskLevels } from '../../types/crm';

// Mock data generators with deterministic seeding
const generateReps = (org) => [
  { id: 'rep-1', name: 'Sarah Johnson', territory: 'Northeast', active: true, email: 'sarah.j@company.com' },
  { id: 'rep-2', name: 'Mike Chen', territory: 'Southeast', active: true, email: 'mike.c@company.com' },
  { id: 'rep-3', name: 'Lisa Rodriguez', territory: 'West Coast', active: true, email: 'lisa.r@company.com' },
  { id: 'rep-4', name: 'David Thompson', territory: 'Midwest', active: true, email: 'david.t@company.com' },
  { id: 'rep-5', name: 'Emily Davis', territory: 'Southwest', active: false, email: 'emily.d@company.com' }
];

const generateDeals = (org) => {
  const companies = [
    'Advanced Dermatology Group', 'Premier Medical Center', 'Wellness Clinic Network',
    'Metro Health Systems', 'Regional Care Associates', 'Elite Healthcare', 
    'Comprehensive Medical Group', 'Innovation Health', 'Unity Medical', 'Apex Healthcare'
  ];
  
  const stages = Object.values(PipelineStages);
  const reps = generateReps(org);
  
  return Array.from({ length: 25 }, (_, i) => ({
    id: `deal-${i + 1}`,
    companyId: `company-${i + 1}`,
    companyName: companies[i % companies.length],
    repId: reps[i % reps.length].id,
    stage: stages[i % stages.length],
    probability: Math.floor(Math.random() * 100),
    expectedClose: new Date(Date.now() + Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    estMonthlyUnits: org === 'bare' ? Math.floor(50 + Math.random() * 200) : Math.floor(20 + Math.random() * 80),
    estMonthlyGMV: org === 'bare' ? Math.floor(5000 + Math.random() * 15000) : Math.floor(8000 + Math.random() * 25000),
    createdAt: new Date(Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
  }));
};

const generateActivities = (org) => {
  const activities = Object.values(ActivityTypes);
  const outcomes = Object.values(ActivityOutcomes);
  const companies = [
    'Advanced Dermatology Group', 'Premier Medical Center', 'Wellness Clinic Network',
    'Metro Health Systems', 'Regional Care Associates'
  ];
  const reps = generateReps(org);
  
  return Array.from({ length: 50 }, (_, i) => ({
    id: `activity-${i + 1}`,
    repId: reps[i % reps.length].id,
    companyId: `company-${i + 1}`,
    companyName: companies[i % companies.length],
    type: activities[i % activities.length],
    outcome: outcomes[i % outcomes.length],
    ts: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    note: `${activities[i % activities.length]} discussion about ${org === 'bare' ? 'Lipoderma' : 'Equicent'} implementation`
  }));
};

const generateAtRisk = (org) => {
  const reasons = Object.values(AtRiskReasons);
  const risks = Object.values(RiskLevels);
  const companies = ['Metro Health Systems', 'Regional Care Associates', 'Elite Healthcare'];
  const reps = generateReps(org);
  
  return companies.map((company, i) => ({
    companyId: `company-${i + 1}`,
    companyName: company,
    repId: reps[i % reps.length].id,
    repName: reps[i % reps.length].name,
    reason: reasons[i % reasons.length],
    lastContact: new Date(Date.now() - (i + 1) * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    risk: risks[i % risks.length]
  }));
};

// Service functions
export const getCrmKpis = async (org, filters = {}) => {
  const deals = generateDeals(org);
  const activities = generateActivities(org);
  
  const wonDeals = deals.filter(d => d.stage === PipelineStages.CLOSED_WON);
  const lostDeals = deals.filter(d => d.stage === PipelineStages.CLOSED_LOST);
  const openDeals = deals.filter(d => ![PipelineStages.CLOSED_WON, PipelineStages.CLOSED_LOST].includes(d.stage));
  
  const now = Date.now();
  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
  
  return {
    totalRevenue: wonDeals.reduce((sum, deal) => sum + (deal.estMonthlyGMV || 0), 0),
    openPipelineUnits: openDeals.reduce((sum, deal) => sum + (deal.estMonthlyUnits || 0), 0),
    winRate90d: wonDeals.length / (wonDeals.length + lostDeals.length) || 0,
    velocityDays: 32, // Mock average
    activeReps: generateReps(org).filter(r => r.active).length,
    activities7d: activities.filter(a => new Date(a.ts).getTime() > sevenDaysAgo).length,
    activities30d: activities.filter(a => new Date(a.ts).getTime() > thirtyDaysAgo).length
  };
};

export const getPipeline = async (org, filters = {}) => {
  return generateDeals(org);
};

export const getReps = async (org) => {
  return generateReps(org);
};

export const getActivities = async (org, filters = {}, limit = 50) => {
  const activities = generateActivities(org);
  return activities.slice(0, limit);
};

export const getAtRisk = async (org, filters = {}) => {
  return generateAtRisk(org);
};

export const getTerritories = async (org) => {
  const reps = generateReps(org);
  const territories = [...new Set(reps.map(r => r.territory))];
  
  return territories.map(territory => ({
    name: territory,
    reps: reps.filter(r => r.territory === territory).length,
    revenue: Math.floor(50000 + Math.random() * 200000),
    deals: Math.floor(5 + Math.random() * 15)
  }));
};
